import React, { useEffect, useRef, useState } from "react";

// the feed APIs don't report progress, so this is an estimate synced to their milestones:
// creating the feed eases towards 30%, fetching posts eases towards 95% and then crawls on to 99% (never further on its own), and only a finished fetch completes to 100%
const FEED_CAP = 30;
const FETCH_CAP = 95;
// after FETCH_CAP, keep creeping very slowly (~0.1% per second) so the number never looks frozen during a long fetch
const CRAWL_CAP = 99;
const CRAWL_STEP = 0.008;
const TICK_MS = 150;
// progress ring geometry (svg units): a gradient border arc plus a light pie fill behind it, both growing clockwise from the top
const RING_SIZE = 96;
const RING_CENTER = RING_SIZE / 2;
const RING_RADIUS = 44;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// pie slice from 12 o'clock to the current progress (the <g> rotates -90deg, so 0deg here points up)
const getPiePath = (progress) => {
    if (progress >= 100) return `M${RING_CENTER - RING_RADIUS} ${RING_CENTER}a${RING_RADIUS} ${RING_RADIUS} 0 1 0 ${RING_RADIUS * 2} 0a${RING_RADIUS} ${RING_RADIUS} 0 1 0 ${-RING_RADIUS * 2} 0Z`;
    const angle = (progress / 100) * 2 * Math.PI;
    const x = RING_CENTER + RING_RADIUS * Math.cos(angle);
    const y = RING_CENTER + RING_RADIUS * Math.sin(angle);
    return `M${RING_CENTER} ${RING_CENTER}L${RING_CENTER + RING_RADIUS} ${RING_CENTER}A${RING_RADIUS} ${RING_RADIUS} 0 ${progress > 50 ? 1 : 0} 1 ${x} ${y}Z`;
};

const getStatus = (progress) => {
    if (progress >= 100) return "All set! Opening your wall...";
    if (progress < FEED_CAP) return "Creating your feed...";
    if (progress < 70) return "Fetching posts...";
    return "Arranging posts on your wall...";
};

const CollectContentSkeleton = ({ feedCreated = false, done = false }) => {
    const [progress, setProgress] = useState(0);
    const stageRef = useRef({ feedCreated, done });
    stageRef.current = { feedCreated, done };

    useEffect(() => {
        const timer = setInterval(() => {
            setProgress(prev => {
                const { feedCreated, done } = stageRef.current;
                if (done) return Math.min(100, prev + Math.max((100 - prev) * 0.3, 2));
                if (feedCreated && prev >= FETCH_CAP - 0.5) return Math.min(CRAWL_CAP, prev + CRAWL_STEP);
                const cap = feedCreated ? FETCH_CAP : FEED_CAP;
                if (prev >= cap) return prev;
                // fast while creating the feed, then slower and slower as posts are fetched
                const rate = feedCreated ? 0.018 : 0.07;
                return Math.min(cap, prev + Math.max((cap - prev) * rate, 0.05));
            });
        }, TICK_MS);
        return () => clearInterval(timer);
    }, []);

    const percent = Math.floor(progress);

    return (
        <div className="ob-panel-body panel-body-p ob-skeleton-panel-body d-flex align-items-center justify-content-center" style={{ minHeight: '90vh' }}>
            <div className="ob-seq-wrapper text-center w-100">
                <div className="d-flex flex-column align-items-center justify-content-center">
                    <div className="ob-collect-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
                        <div className="ob-ring">
                            <svg viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} aria-hidden="true">
                                <defs>
                                    {/* Social Walls logo colors (see BentoLoader.css) */}
                                    <linearGradient id="ob-ring-gradient" x1="0" y1="0" x2="1" y2="1">
                                        <stop offset="0%" stopColor="#F0776F" />
                                        <stop offset="35%" stopColor="#E23A75" />
                                        <stop offset="65%" stopColor="#B02F96" />
                                        <stop offset="100%" stopColor="#5A4BCF" />
                                    </linearGradient>
                                </defs>


                                <g transform={`rotate(-90 ${RING_CENTER} ${RING_CENTER})`}>
                                    <path className="ob-ring-fill" d={getPiePath(progress)} />
                                    <circle className="ob-ring-arc" cx={RING_CENTER} cy={RING_CENTER} r={RING_RADIUS} strokeDasharray={RING_CIRCUMFERENCE} strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress / 100)} />
                                </g>
                            </svg>
                            <div className="ob-ring-percent">{percent}%</div>
                        </div>

                        {/* <h4 className="fw-bold mb-0 fs-4 text-dark d-flex align-items-center justify-content-center gap-3 mt-4">
                            We are fetching posts {" "}
                        </h4> */}
                        <p className="ob-collect-progress-status mb-0">{getStatus(progress)}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CollectContentSkeleton;
